import { type ProviderOffer } from "../../../../types/dtos/api/sales/provider-offer.dto";
import { providerLabelFor } from "./helpers/OfferPricingProviderLabel.helper";
import { type OfferPricingEvaluationContext, type ProviderPricingEvaluation } from "./OfferPricing.types";

/**
 * Contract implemented by one provider pricing plugin.
 *
 * @remarks
 * Each plugin owns the provider-specific pricing and booking URL extraction rules.
 * The registry dispatches provider offers to the corresponding plugin via `providerKey`.
 */
export interface OfferPricingProviderPlugin {

    /**
     * Discriminant key identifying the handled provider.
     */
    readonly "providerKey": ProviderOffer["provider"];

    /**
     * Evaluates a single provider offer payload for a given segment context.
     *
     * @param context Full segment evaluation context with itinerary and preferences.
     * @param providerOffer Provider payload to evaluate.
     * @returns Normalized provider pricing result.
     */
    evaluate(
        context: OfferPricingEvaluationContext,
        providerOffer: ProviderOffer,
    ): ProviderPricingEvaluation;

}

/**
 * Base class for provider pricing plugins.
 *
 * @remarks
 * Use this class to enforce a consistent object-oriented provider shape while
 * keeping provider-specific logic inside subclasses.
 */
export abstract class BaseOfferPricingProvider implements OfferPricingProviderPlugin {

    public readonly providerKey: ProviderOffer["provider"];

    /**
     * Builds a provider plugin base with its discriminant key.
     *
     * @param providerKey Provider identifier handled by this plugin instance.
     */
    protected constructor( providerKey: ProviderOffer["provider"] ) {
        this.providerKey = providerKey;
    }

    /**
     * Returns the default human-readable provider label for this plugin.
     */
    protected getProviderLabel(): string {
        return providerLabelFor( this.providerKey );
    }

    /**
     * Evaluates one provider payload for the current segment context.
     *
     * @param context Segment evaluation context.
     * @param providerOffer Raw provider payload.
     * @returns Normalized provider evaluation for selection policy.
     */
    public abstract evaluate(
        context: OfferPricingEvaluationContext,
        providerOffer: ProviderOffer,
    ): ProviderPricingEvaluation;

}
